import { Button, TOUCH } from "@nymbl/nos2-rules";
import { Link, Star } from "lucide-react";
import { CommentNeeded } from "@/components/time/CommentNeeded";
import { PlanBar } from "@/components/time/PlanBar";
import { TypeChip } from "@/components/time/TypeChip";
import {
  type GridCell as GridCellModel,
  type GridRow,
  type RowStatus,
  rowStatus,
} from "@/lib/time/grid";
import { copyLinkLabel, favoriteLabel, projectLine } from "@/lib/time/labels";
import type { PlanStatus } from "@/lib/time/rules";
import { copyTaskLink } from "@/lib/time/task-link";
import { cn } from "@/lib/utils";

/** A value that did not save. Kept as typed and never sent again on its own. */
export interface Pending {
  raw: string;
  kind: "needs-comment" | "blocked";
  /** The row chip text; null when the error was already shown as a toast. */
  message: string | null;
}

export interface HeldCell {
  cell: GridCellModel;
  p: Pending;
}

/** The held values of one row, in day order. */
export function heldInRow(row: GridRow, pending: Record<string, Pending>): HeldCell[] {
  return row.cells
    .map((cell) => ({ cell, p: pending[cell.key] }))
    .filter((x): x is HeldCell => x.p !== undefined);
}

interface GridTaskInfoProps {
  row: GridRow;
  held: HeldCell[];
  favoritePending: boolean;
  onToggleFavorite: () => void;
  /** Under a project heading the client and project line is left out (the heading says it). */
  grouped?: boolean;
  /** The project heading shows this row's guidance, so the row does not repeat it. */
  planOnHeading?: boolean;
}

/** The over-guidance or blocked chip. */
function StatusChip({ status }: { status: RowStatus }) {
  return (
    <div
      role="status"
      className={cn(
        "mt-1 inline-block rounded-md px-2 py-1 text-xs font-semibold",
        status.tone === "danger" ? "bg-danger-bg text-danger" : "bg-warning-bg text-warning",
      )}
    >
      {status.text}
    </div>
  );
}

/** A project heading's guidance: the bar and the over-guidance chip, lined up with its name. */
export function GroupPlan({ plan }: { plan: PlanStatus }) {
  const status = rowStatus(plan, undefined);
  return (
    <div className="pb-1 pl-6">
      <PlanBar plan={plan} />
      {status && <StatusChip status={status} />}
    </div>
  );
}

/** A grid row's task side: star, name, type, account and project, plan and status chip. */
export function GridTaskInfo({
  row,
  held,
  favoritePending,
  onToggleFavorite,
  grouped = false,
  planOnHeading = false,
}: GridTaskInfoProps) {
  const blockedText =
    held.find((x) => x.p.kind === "blocked" && x.p.message !== null)?.p.message ?? undefined;
  // A held value's block is about this task's cell, so it stays here either way.
  const status = planOnHeading && !blockedText ? null : rowStatus(row.plan, blockedText);
  return (
    <div className="flex items-start gap-1">
      {/* The star's own fixed column, so every title, project and plan line starts at the same x
          whether or not the row can be starred. */}
      <div className="-my-2 -ml-2 w-11 shrink-0 md:-my-1 md:w-9">
        {row.assignable && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={TOUCH}
            aria-pressed={row.isFavorite}
            aria-label={favoriteLabel(row, row.isFavorite)}
            title={row.isFavorite ? "Remove from favorites" : "Add to favorites"}
            disabled={favoritePending}
            onClick={onToggleFavorite}
          >
            <Star
              className={cn(
                "size-4",
                row.isFavorite
                  ? "fill-[var(--brand-accent)] text-[var(--brand-accent)]"
                  : "text-muted-foreground",
              )}
              aria-hidden="true"
            />
          </Button>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{row.taskName}</span>
          <TypeChip type={row.type} />
          {/* A shared link opening Log time on this task (company events, Vinyl's "copy link"). */}
          {row.assignable && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(TOUCH, "-my-2 text-muted-foreground md:-my-1")}
              aria-label={copyLinkLabel(row)}
              title="Copy link"
              onClick={() => void copyTaskLink(row.projectTaskId)}
            >
              <Link className="size-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
        {!grouped && (
          <div className="text-xs text-muted-foreground">
            {projectLine(row.projectName, row.accountName)}
          </div>
        )}
        {!planOnHeading && <PlanBar plan={row.plan} />}
        {status && <StatusChip status={status} />}
      </div>
    </div>
  );
}

interface HeldCommentsProps {
  held: HeldCell[];
  saving: boolean;
  /** Prefix for the box ids, so the desktop and phone layouts never share an id. */
  idPrefix: string;
  onSave: (held: HeldCell, comment: string) => void;
}

/** One comment box per value held for a missing comment. */
export function HeldComments({ held, saving, idPrefix, onSave }: HeldCommentsProps) {
  return held
    .filter((x) => x.p.kind === "needs-comment")
    .map((x) => (
      <CommentNeeded
        key={x.cell.key}
        id={`${idPrefix}comment-${x.cell.key}`}
        label={x.p.message ?? ""}
        saving={saving}
        onSave={(comment) => onSave(x, comment)}
      />
    ));
}
